import React, { useRef, useEffect, useState } from 'react';
import { cn } from '../../lib/utils.ts';

interface ScratchCardProps {
  children: React.ReactNode;
  className?: string;
  onReveal?: () => void;
}

export function ScratchCard({ children, className, onReveal }: ScratchCardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match container
    const resizeObserver = new ResizeObserver(() => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
      initCanvas(ctx, canvas.width, canvas.height);
    });

    resizeObserver.observe(container);
    
    // Initial draw
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;
    initCanvas(ctx, canvas.width, canvas.height);

    return () => resizeObserver.disconnect();
  }, []);

  const initCanvas = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    // Gray Glitter Background
    ctx.fillStyle = '#9CA3AF';
    ctx.fillRect(0, 0, width, height);

    // Add "glitter" noise
    for (let i = 0; i < 500; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? '#FFFFFF' : '#E5E7EB';
      ctx.globalAlpha = Math.random() * 0.6 + 0.2; 
      ctx.beginPath();
      ctx.arc(
        Math.random() * width,
        Math.random() * height,
        Math.random() * 1.5,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    
    // Add text "SCRATCH TO REVEAL"
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 20px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH TO REVEAL', width / 2, height / 2);
  };

  const handleScratch = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || isRevealed) return;

    const rect = canvas.getBoundingClientRect();
    let x, y;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = (e as React.MouseEvent).clientX - rect.left;
      y = (e as React.MouseEvent).clientY - rect.top;
    }

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2);
    ctx.fill();
    
    // Check if enough is scratched to reveal completely
    // (Simplified: just hiding canvas after some interaction for better UX if needed, 
    // but for now we let them scratch nicely)
  };

  return (
    <div ref={containerRef} className={cn("relative inline-block select-none", className)}>
      <div className={cn("w-full h-full transition-opacity duration-500", isRevealed ? "opacity-100" : "opacity-100")}>
        {children}
      </div>
      <canvas
        ref={canvasRef}
        className={cn(
          "absolute inset-0 z-10 touch-none cursor-pointer rounded-sm",
          isRevealed && "pointer-events-none opacity-0 transition-opacity duration-700"
        )}
        onMouseMove={handleScratch}
        onTouchMove={handleScratch}
      />
    </div>
  );
}
